import { PDFDocumentProxy } from "pdfjs-dist";
import { useCallback, useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import pJson from "../package.json";
import MainSideCard from "./MainSideCard";
import PageThumbnails from "./PageThumbnails";
import { useWindowProperties } from "./useWindowProperties";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

type Branding = {
  logo: string | null;
  colour: string;
  teamName: string;
};

function App() {
  const token = window.location.pathname.split("/")[1];
  const windowProperties = useWindowProperties();
  const isDesktop = windowProperties.width >= 1536 ||
    (windowProperties.width >= 1280 && windowProperties.isLandscape);

  const [isLoading, setIsLoading] = useState(true);
  const [documentUrl, setDocumentUrl] = useState<string>();
  const [documentName, setDocumentName] = useState<string>();
  const [branding, setBranding] = useState<Branding | null>(null);
  const [error, setError] = useState<string>();

  const [pdfDocument, setPdfDocument] = useState<PDFDocumentProxy>();
  const [pdfLoading, setPdfLoading] = useState(true);
  const [thumbnailsLoading, setThumbnailsLoading] = useState(false);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollListenerEnabledRef = useRef(true);
  const [selectedPage, setSelectedPage] = useState(1);

  const temporarilyDisableScrollListener = useCallback(() => {
    scrollListenerEnabledRef.current = false;
    setTimeout(() => {
      scrollListenerEnabledRef.current = true;
    }, 600);
  }, []);

  const pageNumbers: Array<number> = Array(pdfDocument?.numPages ?? 0).fill(1).map((one: number, i: number) => one + i);

  const getDocumentMaxWidth = () => {
    if (windowProperties.width >= 3840) return 1360;
    if (windowProperties.width >= 2560) return 1120;
    if (windowProperties.width >= 1920) return 920;
    return 760;
  };
  const pageWidth = Math.min(getDocumentMaxWidth(), windowProperties.width) - 40;

  const calculateVisibility = () => {
    if (!scrollListenerEnabledRef.current) return;
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    let maxVisible = 0;
    let visiblePage = null;

    const pages = scrollContainer.querySelectorAll('.page');
    pages.forEach((page, index) => {
      const rect = page.getBoundingClientRect();
      const containerRect = scrollContainer.getBoundingClientRect();

      const visibleHeight = Math.min(containerRect.bottom, rect.bottom) - Math.max(containerRect.top, rect.top);
      const visibleWidth = Math.min(containerRect.right, rect.right) - Math.max(containerRect.left, rect.left);

      if (visibleHeight > 0 && visibleWidth > 0) {
        const visibleArea = visibleHeight * visibleWidth;
        if (visibleArea > maxVisible) {
          maxVisible = visibleArea;
          visiblePage = index + 1;
        }
      }
    });

    if (visiblePage) {
      setSelectedPage(visiblePage);
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', calculateVisibility);
    return () => {
      container.removeEventListener('scroll', calculateVisibility);
    };
  }, [pageNumbers]);

  useEffect(() => {
    if (isDesktop && documentUrl) {
      setThumbnailsLoading(true);
    }
  }, [isDesktop, documentUrl]);

  const initialFetch = useCallback(async (abortController: AbortController) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/workflows/preview/${token}/`, { signal: abortController.signal });
      if (res.ok) {
        const data = await res.json();
        setDocumentUrl(data.documentUrl);
        setDocumentName(data.documentName);
        setBranding(data.branding ?? null);
      } else {
        setError("Document not found.");
      }
    } catch (err) {
      if (!(err instanceof DOMException && err.name === "AbortError")) {
        setError("Failed to load document.");
      }
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      setIsLoading(false);
      return;
    }
    const abortController = new AbortController();
    initialFetch(abortController);
    return () => abortController.abort();
  }, [token, initialFetch]);

  useEffect(() => {
    document.title = documentName ?? "Portant Preview";
  }, [documentName]);

  function handleLastPageLoad() {
    setPdfLoading(false);
  }

  if (isLoading)
    return (
      <div className="fixed flex w-full h-full bg-gray-50">
        <img src="https://static.portant.co/portant-loading-blue.svg" className="w-32 h-32 m-auto" alt="Loading" />
      </div>
    );

  if (!token)
    return (
      <div className="fixed flex w-full h-full bg-[#F6FAFF] items-center justify-center">
        <div className="text-center text-gray-500 font-title">
          <img src="https://static.portant.co/logo-mark-blue.svg" className="w-24 h-24 mx-auto mb-4" alt="Portant" />
          <a className="text-lg underline" href="https://www.portant.co">www.portant.co</a>
          <p className="text-sm mt-2">v{pJson.version}</p>
        </div>
      </div>
    );

  if (error)
    return (
      <div className="fixed flex w-full h-full bg-[#F6FAFF] items-center justify-center">
        <div className="text-center text-gray-500 font-title">
          <img src="https://static.portant.co/logo-mark-blue.svg" className="w-24 h-24 mx-auto mb-4" alt="Portant" />
          <p className="text-lg">{error}</p>
        </div>
      </div>
    );

  return (
    <div className={`fixed flex flex-col w-screen h-screen justify-center items-center overflow-auto scroll-smooth ${branding?.colour ? "" : "bg-[#F6FAFF]"}`} ref={scrollContainerRef} style={{
      ["--brand-colour" as string]: branding?.colour ?? "#1875fb",
      ["--brand-colour-10" as string]: `${(branding?.colour ?? "#1875fb")}1a`,
    }}>
      {branding?.colour && <div className="h-screen w-screen fixed -z-10 bg-brand opacity-[0.03]" />}
      {(pdfLoading || thumbnailsLoading) && (
        <div className="fixed flex w-screen h-screen z-50 bg-gray-50">
          <img src="https://static.portant.co/portant-loading-blue.svg" className="w-32 h-32 m-auto" alt="Loading" />
        </div>
      )}
      <div className="flex gap-6 xl:gap-5 2xl:gap-6 3xl:gap-8 2k:gap-10 4k:gap-12 h-full w-full px-4 pt-20 pb-4 xl:px-6 xl:py-16 2xl:px-10 2xl:py-24 3xl:px-14 3xl:py-32 2k:px-20 2k:py-40 4k:px-24 4k:py-48 justify-center">
        {isDesktop && documentUrl && (
          <div className="w-24 3xl:w-32 2k:w-36 4k:w-40 relative">
            <PageThumbnails
              documentUrl={documentUrl}
              pageNumbers={pageNumbers}
              selectedPage={selectedPage}
              setSelectedPage={setSelectedPage}
              onLoadSuccess={() => setThumbnailsLoading(false)}
              temporarilyDisableScrollListener={temporarilyDisableScrollListener}
            />
          </div>
        )}
        <Document
          file={documentUrl}
          onLoadSuccess={(doc) => setPdfDocument(doc)}
          className="flex-shrink-0"
          externalLinkTarget="_blank"
          loading={<div className="bg-white overflow-hidden border border-gray-200 h-full" style={{ width: pageWidth }} />}
        >
          <div className="relative flex flex-col items-center pb-40 xl:pb-16 2xl:pb-24 gap-2">
            {pageNumbers.map((n: number) =>
              <div key={n} className="relative" onClick={() => setSelectedPage(n)}>
                <Page
                  width={pageWidth}
                  pageNumber={n}
                  className={`border border-gray-200 overflow-hidden main-page-${n} page`}
                  loading={<div className="opacity-0" />}
                  onLoadSuccess={n === pageNumbers.length ? handleLastPageLoad : undefined}
                />
              </div>
            )}
          </div>
        </Document>
        <div className="absolute xl:relative xl:w-[316px] 2xl:w-[356px] 3xl:w-[436px] 2k:w-[476px] 4k:w-[516px] flex flex-col xl:h-full">
          <div className="fixed z-10 top-0 xl:pt-16 xl:pb-3 xl:h-full xl:w-[316px] 2xl:pt-24 2xl:pb-4 2xl:w-[356px] 3xl:pt-32 3xl:pb-4 3xl:w-[436px] 2k:pt-40 2k:pb-4 2k:w-[476px] 4k:pt-48 4k:pb-4 4k:w-[516px] flex flex-col gap-3">
            <div className="flex flex-col gap-3 flex-1 min-h-0">
              <MainSideCard
                documentName={documentName ?? "Document"}
                documentUrl={documentUrl ?? ""}
                branding={branding}
              />
            </div>
            <div className="hidden xl:block mt-auto shrink-0 pt-2 text-right text-sm text-gray-500 font-title">
              Copyright &copy; 2026 Portant
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
